import { GetShippingConfigurationResponseBodyType } from "@intelligems/ig-types/functions/shipping-configuration/shipping-configuration";
import { MarketShippingConfiguration } from "@intelligems/ig-types/dtos/market-shipping-configuration";
import { ShippingTestMethodDefinitionDto } from "@intelligems/ig-types/dtos/shipping-test-method-definitions";
import { useQueries, UseQueryResult } from "@tanstack/react-query";
import { marketShippingSelectionKey } from "@intelligems/ig-utils/market-shipping-preservation";
import {
  getShippingConfiguration,
  shippingConfigurationKey,
  shippingConfigurationQueryOptions,
} from "@src/api/shipping-configuration/get-shipping-configuration";
import { useGetShippingProfileBackups } from "@src/api/shipping-profile-backups/get-shipping-profile-backups";
import { projectActiveSelectionReview } from "./activeSelectionReview";
import { rateScopeChoices, unavailableRateScopes } from "./rateScopes";
import { marketTargets, selectionUnavailableReason } from "./selection";

interface MarketQueryState {
  isError: boolean;
  shippingModel?: "legacy" | "markets";
  canRead?: boolean;
  /** Undefined until the market's configuration has loaded; null when the market is gone. */
  market: MarketShippingConfiguration | null | undefined;
}

/**
 * Module-level so TanStack Query memoizes the combined result and keeps
 * `markets` referentially stable across renders that change nothing.
 */
const combineMarketQueries = (
  results: UseQueryResult<GetShippingConfigurationResponseBodyType>[]
): {
  markets: MarketShippingConfiguration[];
  states: MarketQueryState[];
  pending: boolean;
  loading: boolean;
  settled: boolean;
} => ({
  markets: results.flatMap((query) =>
    query.data?.market ? [query.data.market] : []
  ),
  states: results.map((query) => ({
    isError: query.isError,
    shippingModel: query.data?.capabilities?.shippingModel,
    canRead: query.data?.capabilities?.canRead,
    market: query.data ? query.data.market : undefined,
  })),
  pending: results.some((query) => query.isPending),
  loading: results.some((query) => query.isPending || query.isFetching),
  settled: results.every((query) => query.isSuccess),
});

export const useMarketSelections = (
  selections: ShippingTestMethodDefinitionDto[],
  enabled: boolean,
  activeExperienceId?: string,
  selectedMarketIds: string[] = []
) => {
  const backups = useGetShippingProfileBackups(
    enabled ? activeExperienceId || "" : "",
    shippingConfigurationQueryOptions
  );
  const ids = [
    ...new Set([
      ...selectedMarketIds,
      ...marketTargets(selections).map((item) => item.marketId),
    ]),
  ];
  const detail = useQueries({
    queries: ids.map((marketId) => ({
      queryKey: [...shippingConfigurationKey, { marketId }],
      queryFn: () => getShippingConfiguration({ marketId }),
      enabled,
      ...shippingConfigurationQueryOptions,
    })),
    combine: combineMarketQueries,
  });
  const markets = detail.markets;
  // No data yet: nothing to review. A background refetch keeps the settled
  // review on screen; `loading` still holds validation and saving until the
  // refetch lands.
  const pending =
    enabled &&
    (detail.pending || (Boolean(activeExperienceId) && backups.isPending));
  const loading =
    enabled &&
    (detail.loading ||
      (Boolean(activeExperienceId) &&
        (backups.isPending || backups.isFetching)));
  const reviewable =
    enabled &&
    Boolean(activeExperienceId) &&
    backups.isSuccess &&
    detail.settled;
  const projection =
    reviewable && activeExperienceId && backups.data
      ? projectActiveSelectionReview(
          markets,
          selections,
          backups.data,
          activeExperienceId
        )
      : { reviewMarkets: markets, beforeTestLabels: {}, issues: [] };
  const allIssues = (() => {
    if (!enabled) {
      return [];
    }
    const targets = marketTargets(selections);
    const issues = selections.flatMap((selection) => {
      if (selection.shippingModel !== "markets") {
        return [
          "This saved selection uses legacy shipping. Remove it and select markets again.",
        ];
      }
      const state = detail.states[ids.indexOf(selection.marketTarget.marketId)];
      if (state?.isError) {
        return [
          "A saved market could not be loaded. Refresh before continuing.",
        ];
      }
      if (state?.market === undefined) {
        return [];
      }
      if (state.shippingModel === "legacy") {
        return [
          "This store now uses legacy shipping. Markets selections cannot be changed.",
        ];
      }
      if (state.canRead === false) {
        return [
          "Markets shipping access is unavailable. Restore Shopify permissions before changing selections.",
        ];
      }
      if (!state.market) {
        return [
          "A saved market no longer exists. Remove its selections and choose again.",
        ];
      }
      if (activeExperienceId && loading) {
        return [];
      }
      const reason = selectionUnavailableReason(
        selection.marketTarget,
        projection.reviewMarkets.find(
          (market) => market.id === selection.marketTarget.marketId
        ) || state.market,
        targets
      );
      return reason ? [reason] : [];
    });
    return [
      ...issues,
      ...projection.issues,
      ...(activeExperienceId && backups.isError
        ? [
            "Before-test shipping backups could not be loaded. Refresh before relying on historical values.",
          ]
        : []),
    ];
  })();
  const selectionKeys = (items: ShippingTestMethodDefinitionDto[]) =>
    JSON.stringify(
      items
        .map((item) =>
          item.shippingModel === "markets"
            ? marketShippingSelectionKey(item.marketTarget)
            : JSON.stringify(item)
        )
        .sort()
    );
  return {
    markets,
    refetchBackups: () =>
      activeExperienceId ? backups.refetch() : Promise.resolve(),
    reviewMarkets: projection.reviewMarkets,
    beforeTestLabels: projection.beforeTestLabels,
    /**
     * The coverages the selections remove, read from the review projection so
     * a started experience whose merchant group was deleted on start keeps
     * the scope choices its rates were authored for.
     */
    rateScopes: rateScopeChoices(selections, projection.reviewMarkets),
    unavailableRateScopes: unavailableRateScopes(
      selections,
      projection.reviewMarkets
    ),
    issues: allIssues,
    saveBlockReason: (
      currentSelections: ShippingTestMethodDefinitionDto[]
    ): string | null => {
      if (!currentSelections.some((item) => item.shippingModel === "markets")) {
        return null;
      }
      if (
        !enabled ||
        selectionKeys(currentSelections) !== selectionKeys(selections)
      ) {
        return "Wait for the current shipping selections to be checked before saving.";
      }
      if (loading) {
        return "Wait for shipping configuration to load before saving.";
      }
      return allIssues[0] ?? null;
    },
    pending,
    loading,
  };
};
